import { ISignupFormValues, IVenue } from "./BusinessOnboard.types";

/* ------------------------------------------------------------------ */
/* Accent palette (scoped to the v2 onboarding screens only)          */
/* Matches the "Banquet First" red brand accent from the mockups.     */
/* ------------------------------------------------------------------ */
export const ACCENT = {
  main: "#b4314a",
  dark: "#95293d",
  soft: "#f7e6ea",
  ring: "#b4314a",
};

/* ------------------------------------------------------------------ */
/* Step metadata — content mirrors the existing signup wording.        */
/* ------------------------------------------------------------------ */
export interface IStepMeta {
  key: string;
  shortLabel: string;
  eyebrow: string;
  title: string;
  subtitle: string;
}

/** The 4 primary steps shown in the stepper (short labels). */
export const businessOnboardStepLabels = [
  "MOBILE",
  "COMPANY",
  "VENUE",
  "PROFILE",
];

/**
 * Header content per step index. Index 0 (Mobile) has an OTP sub-state
 * handled separately via `mobileOtpMeta`.
 */
export const businessOnboardSteps: IStepMeta[] = [
  {
    key: "mobile",
    shortLabel: "MOBILE",
    eyebrow: "STEP 1 OF 4",
    title: "Mobile verification",
    subtitle: "Start with your primary business number.",
  },
  {
    key: "company",
    shortLabel: "COMPANY",
    eyebrow: "STEP 2 OF 4",
    title: "Banquet details",
    subtitle: "Tell us about your banquet business.",
  },
  {
    key: "venues",
    shortLabel: "VENUES",
    eyebrow: "STEP 3 OF 4",
    title: "Setup your venues",
    subtitle: "Name your venue and set its serving capacity.",
  },
  {
    key: "profile",
    shortLabel: "PROFILE",
    eyebrow: "STEP 4 OF 4",
    title: "Complete your profile",
    subtitle: "Create the credentials for your account.",
  },
];

/** OTP sub-state header (still step index 0). */
export const mobileOtpMeta: Pick<IStepMeta, "eyebrow" | "title"> = {
  eyebrow: "STEP 1 OF 4",
  title: "OTP Verification",
};

/**
 * Builds the OTP subtitle with the (masked) mobile number, e.g.
 * "A 6 digit verification code has been sent to +9199***** 02".
 */
export const buildOtpSubtitle = (mobileNumber: string) =>
  `A 6 digit verification code has been sent to ${maskMobileNumber(mobileNumber)}`;

/** Masks the middle digits of a mobile number, keeping dial code + last 2. */
export const maskMobileNumber = (mobile: string): string => {
  if (!mobile) return "";
  const trimmed = mobile.trim();
  // Keep a leading + and any dial-code prefix visible; mask the middle.
  if (trimmed.length <= 4) return trimmed;
  const head = trimmed.slice(0, trimmed.length - 8 > 0 ? trimmed.length - 8 : 2);
  const tail = trimmed.slice(-2);
  return `${head} XXXXX ${tail}`;
};

/** Primary CTA label per step. */
export const primaryCtaLabels = {
  mobile: "Send verification code",
  otp: "Verify & continue",
  company: "Continue",
  venue: "Continue",
  profile: "Create account",
};

/* ------------------------------------------------------------------ */
/* Landing content — reuses the EXISTING landing wording & icons        */
/* (from LandingPage.content.ts). Only the visual design changes; the   */
/* text and icons are kept identical.                                   */
/* ------------------------------------------------------------------ */
export { landingPageContent as businessOnboardLandingContent } from "../SignUp/LandingPage.content";

/** CTA label for the landing (kept as the existing "Get Started"). */
export const landingCta = "Get Started";

/** sessionStorage key used to persist in-progress onboarding across reloads. */
export const ONBOARD_STORAGE_KEY = "business_onboard_progress_v1";

/* ------------------------------------------------------------------ */
/* Form defaults                                                       */
/* ------------------------------------------------------------------ */
export const initialFormValues: ISignupFormValues = {
  company_outlet_name: "",
  mobile: "",
  email: "",
  logo: "",
  user_password: "",
  first_name: "",
  last_name: "",
  status: "ACTIVE",
  confirm_password: "",
  company_outlet_address_line1: "",
  company_outlet_address_line2: "",
  company_outlet_address_city: "",
  company_outlet_address_state_or_province: "",
  company_outlet_address_country: "",
  company_outlet_address_postal_code: "",
  company_outlet_region: "",
  company_outlet_timezone: "",
};

export const createEmptyVenue = (): IVenue => ({
  venue_name: "",
  venue_address: "",
  venue_city: "",
  venue_state: "",
  venue_country: "",
  venue_pincode: "",
  seating_capacity: "",
  floating_capacity: "",
});
